import {
  Box,
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  useMediaQuery,
  useTheme,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { set, cloneDeep, mapValues, pick, omit, omitBy } from "lodash";
import { parsePhoneNumber } from "libphonenumber-js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  useUpdateMapitShipOrderAdminMutation,
  useUpdateMapitShipOrderMutation,
} from "src/_api/mapitShipApi";
import { MapitShipOrderForm } from "src/_modules/MapitShip/views/MapitShipOrders/components";
import { LoadingButton } from "@mui/lab";
import { ORERR_SCHEMA } from "src/_modules/MapitShip/validationSchemas";
import { runJoiValidate, sanitizeShortAddress } from "src/_utils";
import { useDispatch, useSelector } from "react-redux";
import { openConfirmDialog } from "src/_store/entities/confirmationDialog";
import { ROLES } from "src/constants";
import ConfirmPasswordDialogAdmin from "src/_modules/common/Dialogs/ConfirmPasswordDialogAdmin";

const applyBoxTotals = (order) => {
  const boxes = order?.boxes ?? [];
  const totalWeight = boxes.reduce((acc, box) => {
    const weight = Number(box?.weight);
    return acc + (Number.isFinite(weight) ? weight : 0);
  }, 0);
  const totalBoxes = boxes.length;
  set(order, "weight", totalBoxes > 0 ? totalWeight : 0);
};

export default function EnhancedAssignOrdersToMapitshipDialog({
  open,
  onClose,
  orders,
  formattedOrders,
  hideCards,
  isLoading,
  onSuccess,
  warehouseIssues
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { profile } = useSelector((state) => state.entities.profile);
  const [start, setStart] = useState(false);
  const [finished, setFinished] = useState(false);
  const [ordersToEdit, setOrdersToEdit] = useState([]);
  const [hideCardsToEdit, setHideCardsToEdit] = useState([]);

  const [completedOrders, setCompletedOrders] = useState(0);
  const [openAdminDialog, setOpenAdminDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [
    updateOrder,
    {
      isLoading: updateMapitShipLoading,
      error: updateMapitShipError,
      reset: resetUpdateMapitShipOrder,
    },
  ] = useUpdateMapitShipOrderMutation();
  const [
    updateOrderAdmin,
    {
      isLoading: updateMapitShipLoadingAdmin,
      error: updateMapitShipErrorAdmin,
      reset: resetUpdateMapitShipOrderAdmin,
    },
  ] = useUpdateMapitShipOrderAdminMutation();
  const error = updateMapitShipErrorAdmin || updateMapitShipError;
  const loading = updateMapitShipLoading || updateMapitShipLoadingAdmin;
  const [tempOrder, setTempOrder] = useState({});

  const handleOrderChange = useCallback(({ target: { name, value } }) => {
    setTempOrder((prevState) => {
      const updated = cloneDeep(prevState);
      set(updated, name, value);

      const shouldRecalculateBoxes =
        typeof name === "string" && name.startsWith("boxes");
      if (shouldRecalculateBoxes) {
        applyBoxTotals(updated);
      }

      return updated;
    });
  }, []);

  const [validationErrors, setValidationsErrors] = useState(null);
  const dialogRef = useRef();

  // ========== Confirm Update Order =========
  const confirmUpdateOrder = useCallback(
    async (adminPassword) => {
      try {
        if (!ordersToEdit?.length) return;
        const currentOrder = ordersToEdit?.[completedOrders];
        if (!currentOrder) return;

        setValidationsErrors(null);

        let payload = cloneDeep(tempOrder);
        if (!payload) return;

        const coordinates = payload?.user?.location?.coordinates;
        if (Array.isArray(coordinates) && coordinates.length) {
          payload = set(payload, "user.location", [...coordinates]);
        } else if (payload?.user) {
          payload.user.location = [];
        }

        if (payload?.user?.phoneCode) {
          delete payload.user.phoneCode;
        }


        payload.boxes = (payload?.boxes ?? []).map((box) => ({
          //  ...box,
          dimensions: mapValues(box.dimensions ?? {}, (value) => Number(value)),
          weight: Number(box.weight),
        }));

        applyBoxTotals(payload);

        payload.orderItems = (payload?.orderItems ?? []).map((orderItem) => ({
          ...orderItem,
          price: Number(orderItem.price),
        }));

        if (payload.amountPayable !== undefined) {
          payload.amountPayable = Number(payload.amountPayable);
        }

        if (payload.user) {
          payload.user = omit(payload.user, ["email"]);
        }
         if (payload?.user?.address?.countryCode) {
          payload.user.address = omit(payload.user.address, ["countryCode"]);
        }

        if (payload?.user?.address?.shortAddress) {
          payload.user.address.shortAddress = sanitizeShortAddress(
            payload.user.address.shortAddress,
          );
        }

       

        payload.user.address = omitBy(payload.user.address, v => !v);

        let schemaToUse = ORERR_SCHEMA;

        const errors = runJoiValidate(schemaToUse, payload);

        console.log("payload:", payload);
        console.log("Validation errors:", errors);

        if (errors) {
          setValidationsErrors(errors);
          dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }

        try {
          const parsedPhone = payload.user?.phone
            ? parsePhoneNumber(payload.user.phone, { extract: true })
            : null;
          if (parsedPhone) {
            payload.user.phoneCode = `+${parsedPhone.countryCallingCode}`;
            payload.user.phone = parsedPhone.nationalNumber;
          }
        } catch (error) {
          // Ignore parse errors and keep the original phone value
          console.error("Failed to parse phone number", error);
        }
        // const isAdmin = profile.role === ROLES.admin;
        if (profile.role === ROLES.admin) {
          await updateOrderAdmin({
            id: currentOrder.id,
            payload,
            password: adminPassword,
          }).unwrap();
        } else {
          await updateOrder({
            id: currentOrder.id,
            payload,
          }).unwrap();
        }

        setCompletedOrders((prev) => prev + 1);
        dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Failed to update Mapit Ship order", error);
      }
    },
    [
      completedOrders,
      ordersToEdit,
      tempOrder,
      t,
      updateOrder,
      updateOrderAdmin,
      profile?.role,
    ]
  );

  const finishOrderUpdates = useCallback(async () => {
    setFinished(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    onClose(true);
    onSuccess?.();
  }, [onClose, onSuccess]);

  useEffect(() => {
    if (!start) return;
    if (finished) return;
    if (!ordersToEdit?.length) return;
    if (completedOrders >= ordersToEdit.length && ordersToEdit.length > 0) {
      finishOrderUpdates();
    }
  }, [completedOrders, ordersToEdit, start, finished, finishOrderUpdates]);

  useEffect(() => {
    if (!open) return;
    if (start) return;

    setOrdersToEdit(Array.isArray(formattedOrders) ? formattedOrders : []);
    setHideCardsToEdit(Array.isArray(hideCards) ? hideCards : []);
  }, [open, start, formattedOrders, hideCards]);

  useEffect(() => {
    if (!open) {
      setStart(false);
      setCompletedOrders(0);
      setFinished(false);
      setTempOrder({});
      setValidationsErrors(null);
      setOrdersToEdit([]);
      setHideCardsToEdit([]);
      resetUpdateMapitShipOrder();
      resetUpdateMapitShipOrderAdmin();
    }
  }, [open, resetUpdateMapitShipOrder, resetUpdateMapitShipOrderAdmin]);

  // useEffect(() => {
  //   if (finished) {
  //     const timer = setTimeout(() => {
  //       onClose(true);
  //     }, 2000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [finished, onClose]);

  useEffect(() => {
    if (!start) return;
    if (!ordersToEdit.length) return;
    if (completedOrders >= ordersToEdit.length) return;

    const nextOrderDraft = omit(cloneDeep(ordersToEdit[completedOrders]), [
      "id",
    ]);
    if (!nextOrderDraft) return;

    applyBoxTotals(nextOrderDraft);
    setTempOrder(nextOrderDraft);
    setValidationsErrors(null);

    dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [start, ordersToEdit, completedOrders]);

  const merchantOrderNumber = useMemo(() => {
    const order = orders?.find(
      (o) => o.id === ordersToEdit[completedOrders]?.id
    );
    return order?.merchantOrderNumber || "";
  }, [ordersToEdit, completedOrders, orders]);

  return (
    <Dialog
      open={open}
      onClose={(e) => e.preventDefault()}
      maxWidth="lg"
      dir={theme.direction}
      fullScreen={fullScreen}
      fullWidth
    // PaperProps={{
    //     ref: dialogRef
    // }}
    >
      <DialogTitle>{t("Edit Orders")}</DialogTitle>
      <Divider />
      <DialogContent ref={dialogRef}>
        {isLoading && !start && !finished ? (
          <Box
            height="50vh"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!start && (
              <DialogContentText>
                {t(
                  "The following order data must be modified to be processed by Mapit Ship."
                )}
              </DialogContentText>
            )}
            {start && !finished && (
              <>
                <Typography textAlign="center" color="primary" sx={{ mb: 1 }}>
                  {t("assignOrdersToMapitShipTracker", {
                    total: ordersToEdit?.length,
                    remaining: completedOrders,
                  })}
                </Typography>
                <LinearProgress
                  sx={{ mb: 2 }}
                  variant="determinate"
                  value={
                    ordersToEdit?.length
                      ? (completedOrders / ordersToEdit.length) * 100
                      : 0
                  }
                />
                <MapitShipOrderForm
                  tempOrder={tempOrder}
                  handleOrderChange={handleOrderChange}
                  validationErrors={validationErrors}
                  coordinates={tempOrder?.user?.location?.coordinates}
                  orderReferenceNumber={merchantOrderNumber}
                  hideCards={hideCardsToEdit[completedOrders] || {}}
                  warehouseIssues={warehouseIssues}
                />
              </>
            )}
            {finished && (
              <Typography color="success.main">
                {t(
                  "The orders data has been successfully modified and can now be processed by Mapit Ship."
                )}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        {!!error && <Typography color="error">{t(error)}</Typography>}
        <Box mr="auto" />
        <LoadingButton
          variant="contained"
          sx={{ textTransform: "none" }}
          onClick={() => {
            if (!start) {
              onClose();
            } else {
              dispatch(
                openConfirmDialog({
                  open: true,
                  content: t(
                    "When the dialog is closed, the remaining orders will not be transferred Mapit Ship."
                  ),
                  title: t("Warning"),
                  onConfirm: () => {
                    onClose();
                  },
                })
              );
            }
          }}
        >
          {t("Cancel")}
        </LoadingButton>
        {start ? (
          <>
            <LoadingButton
              variant="contained"
              sx={{ textTransform: "none" }}
              loading={loading}
              onClick={() => {
                if (profile?.role === ROLES.admin) {
                  setOpenAdminDialog(true);
                } else {
                  confirmUpdateOrder();
                }
              }}
              disabled={
                finished ||
                !ordersToEdit?.length ||
                completedOrders >= ordersToEdit.length
              }
            >
              {t("Confirm")}
            </LoadingButton>

            <ConfirmPasswordDialogAdmin
              open={openAdminDialog}
              title={"Please enter your password to be able to edite the shipment"}
              password={adminPassword}
              loading={loading}
              onPasswordChange={setAdminPassword}
              onClose={() => {
                setOpenAdminDialog(false);
                setAdminPassword("");
              }}
              onConfirm={async () => {
                await confirmUpdateOrder(adminPassword);
                setOpenAdminDialog(false);
                setAdminPassword("");
              }}
            />
          </>
        ) : (
          <LoadingButton
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={() => setStart(true)}
            disabled={!ordersToEdit.length}
          >
            {t("Start")}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
